import React from 'react';
import PropTypes from 'prop-types';

import styled from 'styled-components';

import { useSSRQuery } from '../graphql';
import { Signup, Login } from '@g4/next-furniture/LoginSignup';
import LOGIN_SIGNUP_TRANSLATIONS_QUERY from './loginSignup/loginSignupTranslations.query.graphql';
import PCI_CONFIGURATION_QUERY from '@g4/next-furniture/LoginSignup/pciConfigurations.query.graphql.js';

import { Box, Spinner, Row, Col, Text } from './Boneyard';
import { peacockBlue } from '@g4/boneyard/lib/theme/colors';
import { withValue } from '@g4/automation-hooks';
import {
  BOOKING_LOGIN,
  BOOKING_SIGNUP,
  BOOKING_FORGOT_PASSWORD,
} from '../../automationHooks';
import { useModal } from './ModalProvider';
import { useAuthentication } from '../graphql';
import ForgotPasswordWrapper, {
  ForgotPasswordTitle,
} from './ForgotPasswordWrapper';
import OPERATOR_LOGOS from './allwaysAllegiantLogos.query.graphql.js';

const PROGRAM_ID = '1';

const ContentWrapper = styled(Box)`
  display: block;
  height: 100%;
`;

const SpinnerWrapper = styled(Box)`
  width: ${props => (props.isMobile ? 'auto' : '400px')};
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const OrWrapper = styled(Box)`
  position: absolute;
  top: 100px;
  left: 50%;
  width: 30px;
  margin-left: -15px;
  padding: 20px 0;
  background-color: rgba(240, 240, 240);
  z-index: 2001;
`;

const BorderedCol = styled(Col)`
  border-right: 1px solid #cfd8dc;
`;

const AllegiantImageWrapper = styled.img`
  max-width: ${props => (props.isMobile ? '94px' : '150px')};
  height: ${props => (props.isMobile ? '36px' : '57.3px')};
`;

const AllwaysImageWrapper = styled.img`
  max-width: ${props => (props.isMobile ? '94px' : '150px')};
  height: ${props => (props.isMobile ? '24px' : '38.8px')};
`;

const ImageContainer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: baseline;
  margin-bottom: 20px;
`;

const LogoSeperator = styled.hr`
  width: 1px;
  height: ${props => (props.isMobile ? '31px' : '50px')};
  margin: ${props => (props.isMobile ? '0 12px' : '9px 21px 0 20px')};
  border-width: 0px;
  background-color: ${props => props.theme.colors.siteHeader.silver};
`;

const propTypes = {
  automationHook: PropTypes.string,
  isMobile: PropTypes.bool,
  redirectOption: PropTypes.func,
  attemptClaimOrder: PropTypes.bool,
  customerEmail: PropTypes.string,
};

const defaultProps = {
  attemptClaimOrder: false,
  customerEmail: '',
};

export const LoginSignupTitle = () => {
  const { loading, error, data } = useSSRQuery(LOGIN_SIGNUP_TRANSLATIONS_QUERY);

  if (loading) {
    return null;
  }

  if (error) {
    throw error;
  }

  const title = data?.application.translations.loginSignUpForm.loginSignupTitle;

  return <>{title}</>;
};

export const LoginSignupHeaderLogo = ({ isMobile }) => {
  const { data: logoData, loading: logoLoading } = useSSRQuery(OPERATOR_LOGOS);

  const allegiantLogo = logoData?.application?.images?.find(
    image => image.id === 'loginPopupAllegiantImage'
  );
  const allwaysLogo = logoData?.application?.images?.find(
    image => image.id === 'loginPopupAllwaysImage'
  );

  if (logoLoading) {
    return null;
  }

  return (
    <ImageContainer isMobile={isMobile}>
      <AllwaysImageWrapper
        alt={allwaysLogo?.alt}
        src={allwaysLogo?.path}
        title={allwaysLogo?.title}
        isMobile={isMobile}
      />
      <LogoSeperator isMobile={isMobile} />
      <AllegiantImageWrapper
        alt={allegiantLogo?.alt}
        src={allegiantLogo?.path}
        title={allegiantLogo?.title}
        isMobile={isMobile}
      />
    </ImageContainer>
  );
};

LoginSignupHeaderLogo.propTypes = {
  isMobile: PropTypes.bool,
};

const LoginSignup = ({
  isMobile,
  redirectOption,
  attemptClaimOrder,
  customerEmail,
}) => {
  const { loading, error, data } = useSSRQuery(LOGIN_SIGNUP_TRANSLATIONS_QUERY);
  const { data: configurations } = useSSRQuery(PCI_CONFIGURATION_QUERY);

  const {
    setShowModal,
    setContent,
    setTitle,
    setIsMobile,
    setAutomationHook,
    unSetModal,
  } = useModal();
  const { signup, login } = useAuthentication();

  if (loading) {
    return (
      <SpinnerWrapper isMobile={isMobile}>
        <Spinner
          size="small"
          appearance="dark"
          automationHook={withValue(BOOKING_LOGIN.hook, 'loading-spinner')}
          inline
        />
      </SpinnerWrapper>
    );
  }

  if (error) {
    throw error;
  }

  const translations = data?.application.translations.loginSignUpForm;

  return (
    <ContentWrapper justifyContent="center">
      <Row gutter={100}>
        {!isMobile && (
          <OrWrapper>
            <Text size={20} weight={500} color={peacockBlue}>
              OR
            </Text>
          </OrWrapper>
        )}
        <Box d="flex" direction={isMobile ? 'column' : 'row'}>
          <BorderedCol>
            <Login
              isMobile={isMobile}
              translations={translations}
              automationHook={BOOKING_LOGIN.hook}
              setShowModal={setShowModal}
              login={login}
              setContent={setContent}
              setTitle={setTitle}
              setIsMobile={setIsMobile}
              setAutomationHook={setAutomationHook}
              unSetModal={unSetModal}
              redirectOption={redirectOption}
              forgotPasswordTitle={<ForgotPasswordTitle />}
              attemptClaimOrder={attemptClaimOrder}
              customerEmail={customerEmail}
              configurations={configurations}
            >
              <ForgotPasswordWrapper
                automationHook={BOOKING_FORGOT_PASSWORD.hook}
                isMobile={isMobile}
                setContent={setContent}
                setTitle={setTitle}
                unsetModal={unSetModal}
                setShowModal={setShowModal}
              />
            </Login>
          </BorderedCol>
          <Col>
            <Signup
              isMobile={isMobile}
              translations={translations}
              programId={PROGRAM_ID}
              automationHook={BOOKING_SIGNUP.hook}
              setShowModal={setShowModal}
              redirectOption={redirectOption}
              signup={signup}
              attemptClaimOrder={attemptClaimOrder}
              setContent={setContent}
              setTitle={setTitle}
              unsetModal={unSetModal}
              configurations={configurations}
            />
          </Col>
        </Box>
      </Row>
    </ContentWrapper>
  );
};

LoginSignup.propTypes = propTypes;
LoginSignup.defaultProps = defaultProps;

export default LoginSignup;
